import { toQueryString } from '@utils/requests';

import { request } from 'api/request';
import { ACTION_TYPE } from 'api/types';

export type AIBuilderMode = 'agent' | 'chat';

export type AIBuilderProps = {
  input: string;
  journeyShortId: string;
  shortId: string;
  files?: File[];
  selectedAction?: {
    id: string;
    type: ACTION_TYPE;
  };
  mode: AIBuilderMode;
};

export type AIBuilderInterruptProps = {
  journeyShortId: string;
  shortId: string;
};

export type QuestionOption = {
  label: string;
  description: string;
};

export type Question = {
  question: string;
  options: QuestionOption[];
  isMultiSelect: boolean;
};

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  text?: string;
  toolName?: string;
  toolCallId?: string;
  toolLabel?: string;
  toolDescription?: string;
  status?: string;
  errorType?: string;
  errorMessage?: string;
  questions?: Question[];
  plan?: PlanData;
};

export type LoadingLabel = 'Thinking' | 'Reasoning' | 'Building workflow' | 'Analyzing the image';

export type ChatSessionResponse = {
  shortId: string;
  mode: AIBuilderMode;
  isGenerating: boolean;
  loadingLabel: LoadingLabel | null;
};

export type MessagesResponse = ChatSessionResponse & {
  messages: ChatMessage[];
};

export type PaginatedMessagesResponse = MessagesResponse & {
  hasMore: boolean;
  oldestMessageId: string | null;
};

export type PaginationParams = {
  limit?: number;
  beforeId?: string;
};

export type NewMessagesParams = {
  afterId: string;
  limit?: number;
};

export type LoadAIBuilderNewMessagesParams = {
  journeyShortId: string;
} & NewMessagesParams;

export type UpdateSessionModeProps = {
  journeyShortId: string;
  mode?: AIBuilderMode;
};

export type UpdateSessionModeResponse = {
  shortId: string;
  mode: AIBuilderMode;
};

export const runAIBuilder = async (data: AIBuilderProps) => {
  const { input, journeyShortId, shortId, files = [], selectedAction, mode } = data;
  const formData = new FormData();

  formData.append('input', input);
  formData.append('journeyShortId', journeyShortId);
  formData.append('shortId', shortId);
  formData.append('mode', mode);

  if (selectedAction) {
    formData.append('selectedAction', JSON.stringify(selectedAction));
  }

  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await request.post('v1/ai-builder/run/', formData);
  return response.data;
};

export const loadAIBuilderMessages = async (journeyShortId: string, params?: PaginationParams) => {
  const queryString = toQueryString({
    limit: params?.limit,
    before_id: params?.beforeId,
  });
  const response = await request.get<PaginatedMessagesResponse>(
    `v1/ai-builder/chat-messages/${journeyShortId}/${queryString}`
  );
  return response.data;
};

export const loadAIBuilderChatSession = async (journeyShortId: string) => {
  const response = await request.get<ChatSessionResponse>(
    `v1/ai-builder/chat-session/${journeyShortId}/`
  );
  return response.data;
};

export const loadAIBuilderNewMessages = async (params: LoadAIBuilderNewMessagesParams) => {
  const { journeyShortId, afterId, limit } = params;
  const queryString = toQueryString({
    after_id: afterId,
    limit,
  });
  const response = await request.get<MessagesResponse>(
    `v1/ai-builder/chat-messages/${journeyShortId}/newer/${queryString}`
  );
  return response.data;
};

export const updateSessionMode = async (data: UpdateSessionModeProps) => {
  const { journeyShortId, mode } = data;
  const response = await request.patch<UpdateSessionModeResponse>(
    `v1/ai-builder/chat-messages/${journeyShortId}/`,
    { mode }
  );
  return response.data;
};

export const deleteAIBuilderHistory = async (journeyShortId: string) => {
  const response = await request.delete(`v1/ai-builder/chat-messages/${journeyShortId}/`);
  return response.data;
};

export const interruptAIBuilder = async (data: AIBuilderInterruptProps) => {
  const response = await request.post('v1/ai-builder/interrupt/', data);
  return response.data;
};

export type TranscribeAudioResponse = {
  text: string;
};

export const transcribeAudio = async (audioFile: File) => {
  const formData = new FormData();
  formData.append('audioFile', audioFile);

  const response = await request.post<TranscribeAudioResponse>(
    'v1/ai-builder/transcribe/',
    formData
  );
  return response.data;
};

export type QuestionAnswer = {
  selectedOptions: string[];
  customText: string;
};

export type AnswerQuestionsProps = {
  journeyShortId: string;
  shortId: string;
  answers: QuestionAnswer[];
};

export const answerQuestions = async (data: AnswerQuestionsProps) => {
  const { journeyShortId, shortId, answers } = data;
  const response = await request.post(
    `v1/ai-builder/answer-questions/${journeyShortId}/${shortId}/`,
    { answers }
  );
  return response.data;
};

// Plan types
export type PlanTaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export type TodoItem = {
  id: string;
  content: string;
  status: PlanTaskStatus;
};

export type PlanTask = {
  id: string;
  name: string;
  description: string;
  status: PlanTaskStatus;
};

export type PlanData = {
  header: string;
  shortDescription: string;
  tasks: PlanTask[];
  planDetails: string;
};

export type AcceptPlanProps = {
  journeyShortId: string;
  shortId: string;
};

export type AcceptPlanResponse = {
  status: 'accepted';
  mode: 'agent';
};

export const acceptPlan = async (data: AcceptPlanProps): Promise<AcceptPlanResponse> => {
  const { journeyShortId, shortId } = data;
  const response = await request.post<AcceptPlanResponse>(
    `v1/ai-builder/accept-plan/${journeyShortId}/${shortId}/`
  );
  return response.data;
};
